import { EVENT } from "@/constants/event";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import { PIN_OPEN_TYPE } from "@/constants/pinOpenType";
import {
  TRANSPARENT_IMG_BG,
  TRANSPARENT_IMG_BG_LIST,
} from "@/constants/transparentImgBg";
import { usePinCardMagnifier } from "@/utils/hooks/usePinCardMagnifier";
import { safeParse } from "@/utils/safeParse";
import useSideNavCollapse, {
  useSideNavVisible,
} from "@/utils/useSideNavCollapse";
import Cookies from "js-cookie";
import { debounce, round } from "lodash";
import { useCallback, useEffect } from "react";
import { useSideNavWidth } from "./useSideNavWidth";

export function getMasonryConf(extraColumns = 0) {
  const pageSettingStr = window.localStorage.getItem(
    LOCAL_STORAGE_KEY.HUABAN_PAGE_SETTING
  );
  const pageSetting = pageSettingStr ? safeParse(pageSettingStr) : null;

  let maxCol = 9;
  if (pageSetting?.columnType === "limit") {
    maxCol = pageSetting?.limit || 9;
  }

  // 如果命中放大镜实验，最大列数限制也 +extraColumns
  maxCol += extraColumns;

  const margin = document.documentElement.clientWidth >= 1600 ? 40 : 24;

  return {
    benchWidth: 240,
    itemGap: 16,
    maxCol,
    minCol: 2,
    mode: MasonryComputeMode.PREFER,
    margin,
  };
}

interface ComputeOption {
  width: number;
  /**瀑布流两边间距 */
  paddingWidth?: number;
  /**卡片宽度 */
  itemWidth?: number;
  /**卡片间距 */
  itemGap?: number;
  /** 页面设置 */
  pageSetting?: any;
}

export function oldComputeColumn({
  width,
  paddingWidth = 24,
  itemWidth = 240,
  itemGap = 16,
  pageSetting,
}: ComputeOption) {
  // 计算当前宽度能展示的最大列数
  let maxNum = Math.floor(
    (width - paddingWidth * 2 + itemGap) / (itemWidth + itemGap)
  );

  let num = maxNum;

  // 应用用户自定义列数设置
  if (pageSetting?.columnType === "limit" && pageSetting?.limit < num) {
    num = pageSetting?.limit;
  }

  // 针对没有设置过列数的用户
  if (!pageSetting?.columnType) {
    num = Math.min(8, num);
  }

  num = Math.max(4, num);

  return {
    num,
    maxNum,
    wrapperWidth: num * (itemWidth + itemGap) - itemGap,
    maxWrapperWidth: maxNum * (itemWidth + itemGap) - itemGap,
    columnSize: itemWidth,
  };
}

export enum MasonryComputeMode {
  MAX,
  MIN,
  PREFER,
}
interface MasonryComputeOption {
  /**容器宽度 */
  containerWidth: number;
  /**模式：最大，最小，最近偏好 */
  mode?: MasonryComputeMode;
  /**基准宽度 */
  benchWidth?: number;
  /**卡片间距 */
  itemGap?: number;
  /**最大列数 */
  maxCol?: number;
  /**最小列数 */
  minCol?: number;
}

/**
 * 计算当前展示宽度下，适合展示的列数，同时此时列数下的卡片宽度；
 * @param option 参数
 *  - contentWidth: 容器宽度
 *  - benchWidth: 基准宽度
 *  - mode: 模式
 *  - itemGap: 卡片间距
 *  - maxCol: 最大列数
 *  - minCol: 最小列数
 * @description #### 不同计算模式：
 * 1. 最大宽度： 以最大宽度为基准， 计算当前容器宽度下，能展示的列数x，计算x列展示的宽度，如果宽度小于容器宽度，则加一列，即x+1列；
 *      - 随着展示宽度变大，临界宽度从最小值趋向于设置的基准宽度
 * 2. 最小宽度： 以最小宽度为基准， 计算当前容器宽度下，能展示的列数x， 计算x列展示的宽度，如果宽度大于容器宽度，则减一列，即x-1列；
 *      - 随着展示宽度变大，临界宽度从最大值趋向于设置的基准宽度
 * 3. 最近偏好： 以设置的偏好宽度基准，计算当前容器宽度下，能展示的列数x， 计算与x与x+1列数情况下的卡片宽度，哪个更接近偏好宽度，则选择哪个；
 *      - 尺寸宽度变化一直在设置的基准宽度附近 [benchWidth*1/2, benchWidth*3/2]
 */
export function masonryCompute({
  containerWidth,
  benchWidth = 240,
  mode = MasonryComputeMode.MAX,
  itemGap = 16,
  maxCol = 9,
  minCol = 2,
}: MasonryComputeOption) {
  //  计算 itemWidth 下，能展示的列数，展示的总宽度
  let cols = Math.floor((containerWidth - itemGap) / (benchWidth + itemGap));

  if (cols > maxCol) {
    cols = maxCol;
  }

  if (cols < minCol) {
    cols = minCol;
  }

  const displayWidth = cols * (benchWidth + itemGap) - itemGap;

  if (mode === MasonryComputeMode.MAX) {
    if (displayWidth < containerWidth && cols < maxCol) {
      return {
        cols: cols + 1,
        columnSize: (containerWidth + itemGap) / (cols + 1) - itemGap,
      };
    } else {
      return {
        cols,
        columnSize: benchWidth,
      };
    }
  }

  if (mode === MasonryComputeMode.MIN) {
    if (displayWidth > containerWidth && cols > minCol) {
      return {
        cols: cols - 1,
        columnSize: (containerWidth - itemGap) / (cols - 1) - itemGap,
      };
    } else {
      return {
        cols,
        columnSize: benchWidth,
      };
    }
  }

  if (mode === MasonryComputeMode.PREFER) {
    const diff = containerWidth - displayWidth;

    if (cols === maxCol) {
      return {
        cols,
        columnSize: benchWidth,
      };
    }

    if (diff > benchWidth / 2 && cols < maxCol) {
      return {
        cols: cols + 1,
        columnSize: (containerWidth + itemGap) / (cols + 1) - itemGap,
      };
    } else {
      return {
        cols,
        columnSize: (containerWidth + itemGap) / cols - itemGap,
      };
    }
  }
}

/**
 * 计算瀑布流列宽展示信息
 * @param isSideNavCollapsed
 * @param hasSideNav
 * @param setStore
 */
export function useMasonryCompute<T extends (store: any) => void>(
  // isSideNavCollapsed: boolean,
  setStore: T
) {
  const [isSideNavCollapsed] = useSideNavCollapse();
  const sideNavWidth = useSideNavWidth();
  const hasSideNav = useSideNavVisible();

  // 获取放大镜实验结果，如果命中实验组则增加2列
  const { extraColumns } = usePinCardMagnifier();

  // 不使用 useMemoizedFn，改用 useCallback，这样可以正确捕获 extraColumns
  const onResize = useCallback(
    async (event?: CustomEvent<boolean>) => {
      // 如果事件存在，则使用事件的值，否则使用传入的值
      const sideNavCollapsed =
        event?.type === EVENT.SIDBAR_COLLAPSED
          ? event?.detail
          : isSideNavCollapsed;
      const clientWidth = document.documentElement.clientWidth;
      const filterPanelWidth =
        event?.type === EVENT.FILTER_VISIBLE_CHANGE &&
        event?.detail &&
        sideNavCollapsed
          ? 248
          : 0;
      const config = getMasonryConf(extraColumns);

      // 计算两边间距，如果侧边栏折叠，则只计算一边间距
      const marginWidth =
        config.margin + (hasSideNav && sideNavCollapsed ? 16 : config.margin);

      // 获取瀑布流展示宽度，最小限制到1120；
      const containerWidth =
        clientWidth - sideNavWidth - marginWidth - filterPanelWidth;

      let imageBackground = TRANSPARENT_IMG_BG.GRID_BG;
      let pinOpenType = PIN_OPEN_TYPE.MODAL;
      let showPinDesc: boolean, showPinUserInfo: boolean, width: number;

      let columnType = "full";

      // 获取用户设置的页面设置
      const pageSettingStr = window.localStorage.getItem(
        LOCAL_STORAGE_KEY.HUABAN_PAGE_SETTING
      );
      const pageSetting = pageSettingStr ? safeParse(pageSettingStr) : null;

      if (pageSetting) {
        imageBackground =
          pageSetting?.imageBackground || TRANSPARENT_IMG_BG.GRID_BG;
        pinOpenType = pageSetting?.pinOpenType || PIN_OPEN_TYPE.MODAL;
        showPinDesc = pageSetting?.showPinDesc;
        showPinUserInfo = pageSetting?.showPinUserInfo;
        columnType = pageSetting?.columnType || "full";
      }

      let cols: number, columnSize: number;

      const result = masonryCompute({
        containerWidth,
        ...config,
      });
      cols = result.cols;
      columnSize = result.columnSize;

      // 如果命中放大镜实验，直接在计算出的列数上 +2
      if (extraColumns > 0) {
        cols = cols + extraColumns;
        // 确保不超过最大列数限制（最大列数限制也要 +extraColumns）
        if (cols > config.maxCol) {
          cols = config.maxCol;
        }

        // 重新计算 columnSize，让增加的列能够适配当前容器宽度
        // 公式：containerWidth = cols * columnSize + (cols - 1) * itemGap
        // 推导：columnSize = (containerWidth + itemGap) / cols - itemGap
        columnSize = (containerWidth + config.itemGap) / cols - config.itemGap;
      }

      columnSize = round(columnSize, 2);
      width = columnSize * cols + config.itemGap * (cols - 1);

      if (width < containerWidth) {
        // 当内容宽度比小容器宽度小是，设置 .calculateWrapper margin-left: auto, 用于居中
        document.body.style.setProperty("--wrapper-left-margin", `auto`);
      } else {
        document.body.style.setProperty("--wrapper-left-margin", `16px`);
      }

      TRANSPARENT_IMG_BG_LIST.forEach((item) => {
        document.body.classList.remove(`transparent-img-${item.value}`);
      });
      document.body.classList.add(`transparent-img-${imageBackground}`);
      document.body.setAttribute("data-column-limit", String(cols));

      // 只存这两项给服务端使用
      Cookies.set(LOCAL_STORAGE_KEY.HUABAN_PAGE_SETTING, {
        cols,
        columnSize,
      });

      setStore({
        columnSize,
        wrapperWidth: width,
        wrapperWidthReady: true,
        column: cols,
        pinOpenType,
        showPinDesc,
        showPinUserInfo,
        maxWrapperWidth: width,
      });
    },
    [extraColumns, isSideNavCollapsed, hasSideNav, sideNavWidth, setStore]
  );

  useEffect(() => {
    const debouncedOnResize = debounce(() => onResize(), 300);

    // 监听页面宽度尺寸变化
    window.addEventListener("resize", debouncedOnResize);
    // 监听配置变化（主要是列数变化）
    window.addEventListener(EVENT.PAGE_SETTING_CHANGE, onResize);
    window.addEventListener(EVENT.SIDBAR_COLLAPSED, onResize);
    window.addEventListener(EVENT.FILTER_VISIBLE_CHANGE, onResize);

    return () => {
      window.removeEventListener("resize", debouncedOnResize);
      window.removeEventListener(EVENT.PAGE_SETTING_CHANGE, onResize);
      window.removeEventListener(EVENT.SIDBAR_COLLAPSED, onResize);
      window.removeEventListener(EVENT.FILTER_VISIBLE_CHANGE, onResize);
    };
  }, [onResize]);

  useEffect(() => {
    onResize();
  }, [hasSideNav, onResize, isSideNavCollapsed, extraColumns]);
}
